import DropdownWithRadio from '@/components/CommonComponents/RadioButtons';
import DatePicker from '@/components/CommonComponents/DatePicker';
import TimePicker from '@/components/CommonComponents/TimePicker';
import { CreateNotifications } from '@/types/notification.type';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import moment from 'moment-timezone';

interface Props {
  messageData: CreateNotifications;
  setMessageData: React.Dispatch<React.SetStateAction<CreateNotifications>>;
}

const ScheduleAndSettings = forwardRef<any, Props>(({ messageData, setMessageData }, ref) => {
  const [sendDate, setSendDate] = useState<string>('');
  const [sendTime, setSendTime] = useState<string>('');
  const [selectedTimezone, setSelectedTimezone] = useState<string>('UTC');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const today = new Date().toISOString().split('T')[0];
  const [dateError, setDateError] = useState('');
  const [timeError, setTimeError] = useState('');

  const timezoneOptions = moment.tz.names();

  const validateTime = (date: string, time: string) => {
    if (!date || !time) return true;

    const [year, month, day] = date.split('-').map(Number);
    const [hour, minute] = time.split(':').map(Number);
    const selected = new Date(year, month - 1, day, hour, minute);

    const now = new Date();
    const minAllowed = new Date(now.getTime() + 15 * 60 * 1000);

    if (selected < minAllowed) {
      setTimeError('Time must be at least 15 minutes from now');
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (!messageData?.scheduledTimer) {
      setSendDate('');
      setSendTime('');
      return;
    }

    try {
      const d = new Date(messageData.scheduledTimer);

      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const mins = String(d.getMinutes()).padStart(2, '0');

      setSendDate(`${yyyy}-${mm}-${dd}`);
      setSendTime(`${hh}:${mins}`);
    } catch (e) {}
  }, [messageData.scheduledTimer]);

  useEffect(() => {
    if (sendDate && sendTime) {
      const local = new Date(`${sendDate}T${sendTime}`);
      const iso = local.toISOString();

      setMessageData((prev) => ({
        ...prev,
        scheduledTimer: iso,
        timeZone: selectedTimezone,
      }));
    }
  }, [sendDate, sendTime, selectedTimezone, setMessageData]);

  const handleScheduleSelect = (value: 'IMMEDIATE' | 'SCHEDULED') => {
    setMessageData((prev) => ({
      ...prev,
      deliveryType: value,
      scheduledTimer: value === 'IMMEDIATE' ? new Date().toISOString() : '',
    }));

    if (value === 'SCHEDULED') {
      setSendDate('');
      setSendTime('');
      setDateError('');
      setTimeError('');
    } else {
      setDateError('');
      setTimeError('');
    }
  };

  useImperativeHandle(ref, () => ({
    validate: () => {
      if (messageData.deliveryType === 'IMMEDIATE') {
        return true;
      }

      if (!sendDate) {
        setDateError('Please select a date');
        return false;
      }

      if (!sendTime) {
        setTimeError('Please select a time');
        return false;
      }

      if (!selectedTimezone) {
        return false;
      }

      return true;
    },
  }));

  const handleDateChange = (date: string) => {
    setSendDate(date);
    setDateError(date ? '' : 'Please select a date');
    setSendTime('');
    setTimeError('');
  };

  const handleTimeChange = (time: string) => {
    if (!sendDate) {
      setTimeError('Please select a date first');
      return;
    }

    if (time && time.length === 5) {
      if (validateTime(sendDate, time)) {
        setSendTime(time);
        setTimeError('');
      } else {
        setTimeError('Time must be at least 15 minutes from now');
      }
    } else {
      setSendTime(time);
      setTimeError('');
    }
  };

  return (
    <div className="">
      <div className="border-b border-[#87074C] px-6 py-5">
        {/* Heading */}
        <h2 className="text-2xl font-bold text-[#621C43]">Schedule & Settings</h2>
        <p className="text-lg  font-medium text-[#621C43]">Choose when to send your notification</p>
      </div>

      <div className="space-y-6 px-6 pt-4">
        <h3 className="font-medium text-[#4A082D] text-lg">
          Send Time <span className="text-red-500">*</span>
        </h3>
        {/* SEND IMMEDIATELY */}
        <div
          className={`p-4 rounded-xl border cursor-pointer transition 
          ${
            messageData.deliveryType === 'IMMEDIATE'
              ? 'border-gray-300 bg-[#FFF8ED]/30'
              : 'border-gray-300 hover:bg-gray-50'
          }`}
          onClick={() => handleScheduleSelect('IMMEDIATE')}
        >
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              checked={messageData.deliveryType === 'IMMEDIATE'}
              onChange={() => handleScheduleSelect('IMMEDIATE')}
              className="h-4 w-4 accent-[#64374c]"
            />
            <span className="font-medium text-[#4A082D]">Send Immediately</span>
          </label>
          <p className="text-xs text-[#4A082D] font-light mt-1 px-6">
            Notification will be sent as soon as you confirm
          </p>
        </div>

        {/* SCHEDULE FOR LATER */}
        <div
          className={`p-4 rounded-xl border cursor-pointer transition 
          ${
            messageData.deliveryType === 'SCHEDULED'
              ? 'border-gray-300 bg-[#FFF8ED]/30'
              : 'border-gray-300 hover:bg-gray-50'
          }`}
          onClick={() => handleScheduleSelect('SCHEDULED')}
        >
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              checked={messageData.deliveryType === 'SCHEDULED'}
              onChange={() => handleScheduleSelect('SCHEDULED')}
              className="h-4 w-4 accent-[#64374c]"
            />
            <span className="font-medium text-[#4A082D]">Schedule for Later</span>
          </label>

          <p className="text-xs text-[#4A082D] font-light mt-1 px-6">
            Choose a specific date and time to send
          </p>
        </div>
        {/* DATE + TIME FIELDS */}
        {messageData.deliveryType === 'SCHEDULED' && (
          <div className="mt-4">
            <h2 className="font-medium px-2 text-[#621C43] mb-3">Schedule Details</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 px-2">
              {/* Date Picker */}
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-1 text-[#621C43]">
                  Select Date <span className="text-red-500">*</span>
                </label>
                <DatePicker
                  value={sendDate}
                  onChange={handleDateChange}
                  placeholder="Select date"
                  minDate={today}
                  error={dateError}
                  className="w-full"
                />
              </div>

              {/* Time Picker */}
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-1 text-[#621C43]">
                  Select Time <span className="text-red-500">*</span>
                </label>
                <TimePicker
                  value={sendTime}
                  onChange={handleTimeChange}
                  placeholder="Select time"
                  error={timeError}
                  step={15}
                  selectedDate={sendDate}
                  className="w-full"
                />
              </div>

              {/* Timezone Picker - Always show when scheduled */}
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-1 text-[#621C43]">
                  Timezone <span className="text-red-500">*</span>
                </label>
                <div className="h-[38px]">
                  <DropdownWithRadio
                    label="Timezone"
                    options={timezoneOptions}
                    selected={selectedTimezone}
                    onChange={(value) => setSelectedTimezone(value || 'UTC')}
                    isOpen={openDropdown === 'timezone'}
                    onOpen={() => setOpenDropdown('timezone')}
                    onClose={() => setOpenDropdown(null)}
                    placeholder="Select Timezone"
                    className="h-full flex flex-row relative border border-gray-300 rounded-lg bg-[#F2E9EA] text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ADDITIONAL SETTINGS */}
      <div className="px-6 border-b py-4 mt-6">
        <h3 className="text-[#4A082D] font-medium mb-3">Additional Settings</h3>

        <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg hover:bg-[#4A082D11] transition-colors">
          <input
            type="checkbox"
            checked={messageData.isTested ?? false}
            onChange={(e) =>
              setMessageData((prev) => ({
                ...prev,
                isTested: e.target.checked,
              }))
            }
            className="h-4 w-4 accent-[#4A082D] rounded"
          />
          <span className="text-sm font-medium text-[#4A082D]">
            Send test notification to me first
          </span>
        </label>
      </div>
    </div>
  );
});

export default ScheduleAndSettings;
